import { createApi } from '@reduxjs/toolkit/query/react';

import type { LanguagesResponse } from '@app/externalApi/content-engine';

import fetchBaseQuery from '../fetchBaseQuery';
import type {
    AppAuthorizationConfiguration,
    AppAuthorizationConfigurationRequest,
    AppAuthorizationConfigurationResponse,
    AppCategory,
    AppConfigurationRequest,
    AppListItem,
    AppModalInfo,
    AppRequest,
    AppRequestAvailability,
    AppSettings,
    EnvResponse,
    GetAppsResponse,
} from './types';

const appsApi = createApi({
    baseQuery: fetchBaseQuery(),
    tagTypes: [],
    endpoints: (builder) => ({
        getProjectApps: builder.query<GetAppsResponse, { projectId: string }>({
            query: ({ projectId }) => ({
                method: 'GET',
                url: `/apps/${projectId}/widget`,
            }),
        }),
        getProjectLocales: builder.query<LanguagesResponse, { projectId: string }>({
            query: ({ projectId }) => ({
                method: 'GET',
                url: `/apps/${projectId}/ce/locales`,
            }),
        }),
        getAppsList: builder.query<AppListItem[], { projectId: string }>({
            query: ({ projectId }) => ({
                method: 'GET',
                url: `/apps/${projectId}/list`,
            }),
        }),
        getCategories: builder.query<AppCategory[], { projectId: string }>({
            query: ({ projectId }) => ({
                method: 'GET',
                url: `/apps/${projectId}/categories`,
            }),
        }),
        getAppDetails: builder.query<AppModalInfo, { projectId: string; slug: string }>({
            query: ({ projectId, slug }) => ({
                method: 'GET',
                url: `/apps/${projectId}/info/${slug}`,
            }),
        }),
        getAppRequestAvailability: builder.query<AppRequestAvailability, void>({
            query: () => ({
                method: 'GET',
                url: `/integration/app-request/availability`,
            }),
        }),
        appRequest: builder.mutation<void, AppRequest>({
            query: (payload) => ({
                url: `/integration/app-request/submit`,
                method: 'POST',
                body: {
                    description: payload.description,
                    useCases: payload.useCases,
                },
            }),
        }),
        getAppSettings: builder.query<AppSettings, { projectId: string; slug: string }>({
            query: ({ projectId, slug }) => ({
                method: 'GET',
                url: `/apps/${projectId}/${slug}/settings`,
            }),
        }),
        getAppConfig: builder.query<AppConfigurationRequest, { projectId: string; slug: string }>({
            query: ({ projectId, slug }) => ({
                method: 'GET',
                url: `/apps/${projectId}/${slug}/config`,
            }),
        }),
        appConfigRequest: builder.mutation<void, { projectId: string; slug: string; data: AppConfigurationRequest }>({
            query: ({ projectId, slug, data }) => ({
                url: `/apps/${projectId}/${slug}/config`,
                method: 'POST',
                body: data,
            }),
        }),
        getAppAuthorizationConfig: builder.query<AppAuthorizationConfiguration, { projectId: string; slug: string }>({
            query: ({ projectId, slug }) => ({
                method: 'GET',
                url: `/apps/${projectId}/${slug}/authorization/config`,
            }),
        }),
        appAuthorizationConfigRequest: builder.mutation<
            AppAuthorizationConfigurationResponse,
            { projectId: string; slug: string; data: AppAuthorizationConfigurationRequest }
        >({
            query: ({ projectId, slug, data }) => ({
                url: `/apps/${projectId}/${slug}/authorization/config`,
                method: 'POST',
                body: data,
            }),
        }),
        removeApp: builder.mutation<void, { projectId: string; slug: string }>({
            query: ({ projectId, slug }) => ({
                url: `/integration/${projectId}/${slug}`,
                method: 'DELETE',
            }),
        }),
        exportApp: builder.mutation<void, { projectId: string; slug: string }>({
            query: ({ projectId, slug }) => ({
                url: `/apps/${projectId}/${slug}/cdn-export`,
                method: 'POST',
            }),
        }),
        getEnv: builder.query<EnvResponse, { projectId: string }>({
            query: ({ projectId }) => ({
                method: 'GET',
                url: `/apps/${projectId}/ce/env`,
            }),
        }),
        getContentManagementApps: builder.query<AppListItem[], { projectType: string }>({
            query: ({ projectType }) => ({
                method: 'GET',
                url: `/apps/list?project_type=${projectType}`,
            }),
        }),
    }),
});

export const {
    useLazyGetProjectAppsQuery,
    useGetAppsListQuery,
    useGetCategoriesQuery,
    useLazyGetAppRequestAvailabilityQuery,
    useAppRequestMutation,
    useGetAppDetailsQuery,
    useGetAppSettingsQuery,
    useGetAppConfigQuery,
    useGetAppAuthorizationConfigQuery,
    useLazyGetAppConfigQuery,
    useAppConfigRequestMutation,
    useRemoveAppMutation,
    useExportAppMutation,
    useAppAuthorizationConfigRequestMutation,
    useGetEnvQuery,
    useLazyGetProjectLocalesQuery,
    useGetContentManagementAppsQuery,
} = appsApi;

export * from './types';
export default appsApi;
